import GooglePayButton from '@google-pay/button-react';
import {
  Check,
  CheckCircle2,
  Loader2,
  ShieldCheck,
  AlertCircle,
  LogOut,
} from 'lucide-react';
import { useState, useMemo } from 'react';
import { User, db, doc, setDoc, addDoc, collection, logout } from '../firebase';
import { useAppSettings } from '../hooks/useAppSettings';

interface PaywallScreenProps {
  user: User;
  theme: 'light' | 'dark' | 'system';
}

interface GooglePaymentData {
  paymentMethodData: {
    tokenizationData: {
      token: string;
    };
  };
}

export function PaywallScreen({ user, theme }: PaywallScreenProps) {
  const [loading, setLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const config = useAppSettings();

  const isDark = useMemo(() => {
    if (theme === 'system') {
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    return theme === 'dark';
  }, [theme]);

  // Google Pay brand guidelines: Use white button on dark backgrounds, black on light.
  const buttonColor = isDark ? 'white' : 'black';

  const handlePaymentSuccess = async (paymentData: GooglePaymentData) => {
    setLoading(true);
    setError(null);
    try {
      console.log('Payment Data:', paymentData);
      // 1. Extract the Stripe Token from Google Pay data
      const stripeToken = JSON.parse(
        paymentData.paymentMethodData.tokenizationData.token
      );
      console.log('Stripe Token ID:', stripeToken.id);

      // 2. Call your backend (e.g., a Firebase Cloud Function) to process the charge
      const response = await fetch(
        'https://us-central1-kowope-finance.cloudfunctions.net/processStripePayment ',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            tokenId: stripeToken.id,
            amount: Math.round(Number(config.price) * 100), // Stripe expects cents
            currency: config.currencyCode.toLowerCase(),
            uid: user.uid,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message || 'Payment failed. Please try again.'
        );
      }

      // 3. Only if the backend call succeeds, update Firestore
      // (In a production app, it's even better to let a Stripe Webhook update this)

      // Update user document
      await setDoc(
        doc(db, 'users', user.uid),
        {
          hasPaid: true,
          paymentDate: new Date().toISOString(),
          plan: 'monthly_subscription',
          paymentMethod: 'google_pay',
          subscriptionStatus: 'active',
        },
        { merge: true }
      );

      // Record payment history
      await addDoc(collection(db, 'users', user.uid, 'app_payments'), {
        date: new Date().toISOString(),
        amount: Number(config.price),
        currency: config.currencyCode,
        status: 'success',
        method: 'google_pay',
      });

      setLoading(false);
      setIsSuccess(true);
    } catch (error) {
      console.error('Payment recording failed', error);
      setError(
        'Payment confirmed by Google, but failed to update your account. Please check your permissions.'
      );
      setLoading(false);
    }
  };

  if (isSuccess) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-emerald-50 p-6 font-sans transition-colors duration-300 dark:bg-black">
        <div className="w-full max-w-md space-y-6 rounded-[40px] border border-emerald-100 bg-white p-10 text-center shadow-2xl dark:border-zinc-800 dark:bg-zinc-900">
          <div className="mx-auto mb-4 flex h-24 w-24 items-center justify-center rounded-full bg-emerald-500 shadow-xl shadow-emerald-200 dark:shadow-emerald-900/20">
            <CheckCircle2 className="h-12 w-12 text-white" />
          </div>

          <div className="space-y-2">
            <h1 className="text-3xl font-bold tracking-tight text-zinc-900 dark:text-zinc-100">
              Payment Successful!
            </h1>
            <p className="text-sm leading-relaxed text-zinc-500 dark:text-zinc-400">
              Thank you for subscribing. Your premium features are now unlocked.
            </p>
          </div>

          <button
            onClick={() => window.location.reload()} // Reload to ensure app state refreshes
            className="w-full rounded-2xl bg-zinc-900 py-4 font-bold text-white shadow-lg transition-opacity hover:opacity-90 active:scale-95 dark:bg-zinc-100 dark:text-zinc-900"
          >
            Continue to App
          </button>
        </div>
      </div>
    );
  }

  if (!config.stripePublishableKey) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-zinc-50 dark:bg-black">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-zinc-50 p-6 font-sans transition-colors duration-300 dark:bg-black">
      <div className="w-full max-w-md space-y-8 rounded-[40px] border border-zinc-100 bg-white p-8 text-center shadow-2xl dark:border-zinc-800 dark:bg-zinc-900">
        <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-3xl bg-indigo-600 shadow-xl shadow-indigo-200 dark:shadow-indigo-900/20">
          <ShieldCheck className="h-10 w-10 text-white" />
        </div>

        <div className="space-y-2">
          <h1 className="text-3xl font-bold tracking-tight text-zinc-900 dark:text-zinc-100">
            Unlock Kówópé
          </h1>
          <p className="text-sm leading-relaxed text-zinc-500 dark:text-zinc-400">
            Get complete control over your finances with our premium features.
          </p>
        </div>

        <div className="space-y-3 rounded-3xl border border-zinc-100 bg-zinc-50 p-6 text-left dark:border-zinc-800 dark:bg-zinc-950/50">
          {config.features.map((feature: string, i: number) => (
            <div key={i} className="flex items-center gap-3">
              <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-100 dark:bg-emerald-900/30">
                <Check className="h-3 w-3 text-emerald-600 dark:text-emerald-400" />
              </div>
              <span className="text-sm font-medium text-zinc-700 dark:text-zinc-300">
                {feature}
              </span>
            </div>
          ))}
        </div>

        <div className="pt-2">
          <div className="mb-4">
            <span className="text-3xl font-bold text-zinc-900 dark:text-zinc-100">
              {error && (
                <div className="mb-4 flex items-center gap-3 rounded-2xl border border-rose-100 bg-rose-50 p-4 text-left text-rose-700 dark:border-rose-900/50 dark:bg-rose-900/20 dark:text-rose-400">
                  <AlertCircle className="h-5 w-5 shrink-0" />
                  <p className="text-sm font-medium">{error}</p>
                </div>
              )}
              ${config.price}
            </span>
            <span className="text-sm font-medium text-zinc-500 dark:text-zinc-400">
              {' '}
              / month
            </span>
          </div>

          {loading ? (
            <div className="flex w-full items-center justify-center rounded-2xl bg-zinc-100 py-4 dark:bg-zinc-800">
              <Loader2 className="h-6 w-6 animate-spin text-zinc-500" />
            </div>
          ) : (
            <div className="mt-2 w-full overflow-hidden rounded-2xl shadow-lg transition-transform active:scale-95">
              <GooglePayButton
                environment={
                  config.environment === 'TEST' ? 'TEST' : 'PRODUCTION'
                }
                paymentRequest={{
                  apiVersion: 2,
                  apiVersionMinor: 0,
                  allowedPaymentMethods: [
                    {
                      type: 'CARD',
                      parameters: {
                        allowedAuthMethods: ['PAN_ONLY', 'CRYPTOGRAM_3DS'],
                        allowedCardNetworks: ['MASTERCARD', 'VISA', 'AMEX'],
                        billingAddressRequired: true,
                        billingAddressParameters: {
                          format: 'FULL',
                        },
                      },
                      tokenizationSpecification: {
                        type: 'PAYMENT_GATEWAY',
                        parameters: {
                          gateway: 'stripe',
                          'stripe:version': '2026-01-28',
                          'stripe:publishableKey': String(
                            config.stripePublishableKey
                          ),
                        },
                      },
                    },
                  ],
                  merchantInfo: {
                    merchantId: String(config.googlePayMerchantId),
                    merchantName: String(config.merchantName),
                  },
                  transactionInfo: {
                    totalPriceStatus: 'FINAL',
                    totalPriceLabel: 'Monthly Subscription',
                    totalPrice: String(config.price),
                    currencyCode: String(config.currencyCode),
                    countryCode: String(config.countryCode),
                  },
                }}
                onLoadPaymentData={handlePaymentSuccess}
                buttonType="subscribe"
                buttonColor={buttonColor}
                buttonSizeMode="fill"
                buttonRadius={16}
                style={{ width: '100%', height: 56 }}
              />
            </div>
          )}

          <button
            onClick={() => logout()}
            className="mt-4 flex w-full items-center justify-center gap-2 py-2 text-xs font-bold text-zinc-400 transition-colors hover:text-rose-500"
          >
            <LogOut className="h-4 w-4" />
            Sign Out
          </button>

          <p className="mt-4 text-[10px] text-zinc-400">
            Secured by Google Pay. Cancel anytime.
          </p>
        </div>
      </div>
    </div>
  );
}
